import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  RateLimitMonitor,
  RecentBatchTimings,
  RateLimitConfigEditor,
  SharePointRateLimitMonitor,
  SharePointRateLimitConfigEditor,
} from "../components/RateLimitMonitor";
import { ActiveFunctionsPanel } from "../components/ActiveFunctionsPanel";
import { RunningTasksPanel } from "../components/scheduler/RunningTasksPanel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { getRateLimitConfig, getRateLimitHealth, RateLimitConfig } from "../lib/api";
import Header from "../components/layout/header";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { MonitoringLayout } from "@/pages/monitoring/layout";

export default function RateMonitorPage() {
  const [config, setConfig] = useState<RateLimitConfig | undefined>();

  const fetchConfig = useCallback(async () => {
    const response = await getRateLimitConfig();
    if (response.success && response.config) {
      setConfig(response.config);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Fetch rate limit health including worker data
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['rateLimitHealth'],
    queryFn: getRateLimitHealth,
    refetchInterval: 5000, // Poll every 5 seconds
  });

  const queueWorkers = healthData?.queueWorkers;

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view API Rate Monitor</p>
        </div>
      </div>
    }>
      <MonitoringLayout>
        <div className="flex flex-col h-full">
          <Header
            title="API Rate Monitor"
          />
          <main className="flex-1 overflow-y-auto bg-[#F5F5F5] p-4">
            <div className="mb-4">
              <p className="text-gray-600">Monitor queue workers, running jobs, and API rate limits for Dataverse and SharePoint</p>
            </div>
            <div className="w-full space-y-6">
              {/* Row 1: Running Jobs */}
              <RunningTasksPanel />

              {/* Row 2: Active Functions + Rate Limit Status */}
              <div className="grid gap-6 lg:grid-cols-3">
                <ActiveFunctionsPanel
                  activeWorkers={queueWorkers?.activeWorkers || []}
                  isLoading={healthLoading}
                />
                <RateLimitMonitor />
                <SharePointRateLimitMonitor />
              </div>

              {/* Row 3: Config Editors */}
              <div className="grid gap-6 lg:grid-cols-2">
                <RateLimitConfigEditor
                  currentConfig={config}
                  onConfigUpdated={fetchConfig}
                />
                <SharePointRateLimitConfigEditor />
              </div>

              {/* Row 4: Batch Timings */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Batch Timings</CardTitle>
                  <CardDescription>Last 10 SQL Sync batches with performance metrics (5-minute window)</CardDescription>
                </CardHeader>
                <CardContent>
                  <RecentBatchTimings />
                </CardContent>
              </Card>

              {/* Row 5: How it works */}
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                  <CardDescription>Azure Functions elastic scaling, token bucket, and rate limiting explained</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="grid gap-6 md:grid-cols-3">
                    <div>
                      <h3 className="font-semibold mb-2">Elastic Azure Functions</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Azure <strong>scales function instances dynamically</strong> based on queue depth</li>
                        <li>Typically runs 30-50 concurrent function instances during sync</li>
                        <li>Each instance processes one queue message at a time</li>
                        <li>Instances spin up/down automatically as needed</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Dataverse Rate Limiting</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li><strong>1 token = 1 batch request</strong> (30 records per batch)</li>
                        <li>Functions must acquire a token before calling Dataverse</li>
                        <li>Tokens refill at configured rate (default: 5/sec)</li>
                        <li>Burst limit caps max concurrent requests (max 52)</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">SharePoint Rate Limiting</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li><strong>1 token = 1 Graph API request</strong></li>
                        <li>Separate from Dataverse limits</li>
                        <li>Controls folder scanning and file operations</li>
                        <li>Prevents Graph API throttling (429 errors)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <h3 className="font-semibold mb-2">Processing Flow</h3>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <span className="px-2 py-1 bg-gray-100 rounded">Queue Message</span>
                      <span>→</span>
                      <span className="px-2 py-1 bg-blue-100 rounded">Function Instance</span>
                      <span>→</span>
                      <span className="px-2 py-1 bg-yellow-100 rounded">Acquires Token</span>
                      <span>→</span>
                      <span className="px-2 py-1 bg-green-100 rounded">Sends to Dataverse</span>
                      <span>→</span>
                      <span className="px-2 py-1 bg-gray-100 rounded">Complete</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <h3 className="font-semibold mb-1">Status indicators:</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li><span className="text-green-600 font-medium">HEALTHY</span> - Load &lt;80%, normal operation</li>
                      <li><span className="text-yellow-600 font-medium">ELEVATED</span> - Load 80-95%, backpressure applied</li>
                      <li><span className="text-red-600 font-medium">CRITICAL</span> - Load &gt;95%, new syncs may be delayed</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </MonitoringLayout>
    </ViewGuard>
  );
}
